import React from 'react';
import {
  Box, Typography, Card, CardContent, Button, Grid, Divider, Chip, Stack
} from '@mui/material';
import { GetApp, Apple, Android, Window, Info } from '@mui/icons-material';

/**
 * DownloadsView – provides download links for Sabal applications
 * and instructions for iOS TestFlight / Ad-Hoc access via UDID registration.
 *
 * Restored from commit bc99d41, updated for current architecture:
 *  - Hanko auth (no more @sabalcanopy.com requirement)
 *  - Users may have any email domain (e.g. @sunoco.com)
 *  - iOS via UDID registration at udid.io
 *  - Android APK direct download
 *  - Windows EXE direct download (when available)
 */
const DownloadsView = () => {
  const appDownloads = [
    {
      title: 'Sabal App for Android',
      icon: <Android fontSize="large" sx={{ color: '#3DDC84' }} />,
      description: 'Mobile version for Android devices. Tap Download, then open the APK to install.',
      link: '/downloads/SabalApp_v1.3.0.apk',
      available: true,
      version: 'v1.3.0',
    },
    {
      title: 'Sabal App for Windows',
      icon: <Window fontSize="large" color="primary" />,
      description: 'Desktop version for Windows monitoring and control.',
      link: '/downloads/sabal_app_setup_1.3.0.exe',
      available: true,
      version: 'v1.3.0',
    },
  ];

  return (
    <Box sx={{ maxWidth: 1200, margin: '0 auto', p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
        Downloads
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Access the latest versions of the Sabal applications and resources.
      </Typography>

      <Grid container spacing={3}>
        {/* Direct-download app cards */}
        {appDownloads.map((app) => (
          <Grid item xs={12} sm={6} md={4} key={app.title}>
            <Card sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 2,
              boxShadow: 2,
              opacity: app.available ? 1 : 0.7,
            }}>
              <CardContent sx={{ flexGrow: 1, textAlign: 'center', pt: 4 }}>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                  {app.icon}
                </Box>
                <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                  {app.title}
                </Typography>
                <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 1 }}>
                  <Chip label={app.version} size="small" variant="outlined" />
                  {!app.available && (
                    <Chip label="Coming Soon" size="small" color="warning" />
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                  {app.description}
                </Typography>
              </CardContent>
              <Box sx={{ p: 2.5, pt: 0 }}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<GetApp />}
                  sx={{ borderRadius: 1.5, py: 1 }}
                  component="a"
                  href={app.link}
                  download
                  disabled={!app.available}
                >
                  {app.available ? 'Download' : 'Not Yet Available'}
                </Button>
              </Box>
            </Card>
          </Grid>
        ))}

        {/* iOS UDID Registration Card */}
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            boxShadow: 2,
            border: '1px solid #e0e0e0',
          }}>
            <CardContent sx={{ flexGrow: 1, textAlign: 'center', pt: 4 }}>
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                <Apple fontSize="large" sx={{ color: '#555' }} />
              </Box>
              <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                Sabal App for iOS
              </Typography>
              <Chip label="By Request" size="small" color="info" sx={{ mt: 1 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1, textAlign: 'left' }}>
                To install on iPhone we need to register your device first:
              </Typography>
              <Box component="ol" sx={{
                textAlign: 'left',
                pl: 2.5,
                '& li': { mb: 1, fontSize: '0.875rem', color: 'text.secondary' },
              }}>
                <li>
                  On your iPhone, open Safari and visit{' '}
                  <strong>
                    <a href="https://udid.io" target="_blank" rel="noopener noreferrer">
                      udid.io
                    </a>
                  </strong>
                </li>
                <li>Follow the prompts to retrieve your device UDID (this is safe)</li>
                <li>Copy the UDID and email it to{' '}
                  <strong>
                    <a href="mailto:sabaladmin@sabalcanopy.com?subject=iOS%20UDID%20Registration">
                      sabaladmin@sabalcanopy.com
                    </a>
                  </strong>
                </li>
                <li>We'll register your device and send you a download link</li>
              </Box>
            </CardContent>
            <Box sx={{ p: 2.5, pt: 0 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Info />}
                sx={{ borderRadius: 1.5, py: 1 }}
                component="a"
                href="https://udid.io"
                target="_blank"
                rel="noopener noreferrer"
              >
                Get Your UDID
              </Button>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Other Resources */}
      <Box sx={{ mt: 6 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Other Resources
        </Typography>
        <Divider sx={{ mb: 2.5 }} />
        <Card sx={{ bgcolor: '#f8f9fa', border: '1px dashed #dee2e6' }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              Additional documentation, configuration tools, and firmware updates
              will be available here soon.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default DownloadsView;
